import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { updateLastActivity } from "@/utils/sessionStorage";
import { Globe, ChevronRight, Zap, Power } from "lucide-react";
import { SettingsCard } from "./SettingsCard";

type ConnectionApiType = 'waha' | 'coexistent';

const formatPhoneNumber = (number?: string): string => {
  if (!number) return '';
  const digits = number.replace(/\D/g, '');

  if (digits.startsWith('55') && digits.length >= 12) {
    const country = digits.slice(0, 2);
    const ddd = digits.slice(2, 4);
    const rest = digits.slice(4);
    if (rest.length === 9) return `+${country} (${ddd}) ${rest.slice(0, 5)}-${rest.slice(5)}`;
    if (rest.length === 8) return `+${country} (${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
  }

  return `+${digits}`;
};

const extractOwnerNumber = (ownerJid?: string): string => {
  if (!ownerJid) return '';
  return ownerJid
    .replace(/@.*$/, '')
    .replace(/:\d+$/, '')
    .replace(/\D/g, '');
};

const checkEnvironmentVariables = (): { isValid: boolean; error?: string } => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || supabaseUrl === 'your_supabase_url_here') {
    return { isValid: false, error: 'VITE_SUPABASE_URL não está configurada' };
  }

  if (!supabaseAnonKey || supabaseAnonKey === 'your_supabase_anon_key_here') {
    return { isValid: false, error: 'VITE_SUPABASE_ANON_KEY não está configurada' };
  }

  return { isValid: true };
};

interface ConnectionConflict {
  conflict_account_name: string;
  conflict_account_id: string;
  owner_jid: string;
}

interface ConnectionStatus {
  hasInstance: boolean;
  isConnected: boolean;
  qrCode?: string;
  instanceName?: string;
  ownerNumber?: string;
  loading: boolean;
  error?: string;
  conflict?: ConnectionConflict;
}

const ApiTypeSelector: React.FC<{
  selected: ConnectionApiType;
  onSelect: (type: ConnectionApiType) => void;
}> = ({ selected, onSelect }) => {
  const cardBase =
    "flex items-center gap-3 p-4 rounded-xl border text-left transition-colors duration-200";
  const cardIdle =
    "bg-white border-[#E5E7EB] text-[#0F172A] hover:bg-[#F8FAFC] hover:border-[#BFDBFE] dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#FAFAFA] dark:hover:bg-[#3F3F46] dark:hover:border-blue-500/40";
  const cardActive =
    "bg-[#EEF4FF] border-[#2563EB] text-[#0F172A] dark:bg-blue-500/15 dark:border-[#2563EB] dark:text-[#FAFAFA]";

  return (
    <div className="mb-8">
      <h3 className="text-sm font-semibold text-[#64748B] mb-3 uppercase tracking-wide dark:text-[#71717A]">
        Tipo de Conexão
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
        <button
          type="button"
          onClick={() => onSelect('waha')}
          aria-label="Selecionar Versão 3"
          aria-pressed={selected === 'waha'}
          className={`${cardBase} ${selected === 'waha' ? cardActive : cardIdle}`}
        >
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            selected === 'waha'
              ? 'bg-[#DBEAFE] dark:bg-blue-500/20'
              : 'bg-[#F1F5F9] dark:bg-[#3F3F46]'
          }`}>
            <Zap className={`w-5 h-5 ${selected === 'waha' ? 'text-[#2563EB] dark:text-[#60A5FA]' : 'text-[#64748B] dark:text-[#A1A1AA]'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">
              Versão 3
            </p>
            <p className="text-xs text-[#64748B] dark:text-[#71717A] mt-0.5">Conexão via QR Code</p>
          </div>
          {selected === 'waha' && (
            <div className="w-2 h-2 rounded-full bg-[#2563EB] dark:bg-[#60A5FA] flex-shrink-0" />
          )}
        </button>

        <button
          type="button"
          onClick={() => onSelect('coexistent')}
          aria-label="Selecionar API Oficial (Coexistente)"
          aria-pressed={selected === 'coexistent'}
          className={`${cardBase} ${selected === 'coexistent' ? cardActive : cardIdle}`}
        >
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            selected === 'coexistent'
              ? 'bg-[#DBEAFE] dark:bg-blue-500/20'
              : 'bg-[#F1F5F9] dark:bg-[#3F3F46]'
          }`}>
            <Globe className={`w-5 h-5 ${selected === 'coexistent' ? 'text-[#2563EB] dark:text-[#60A5FA]' : 'text-[#64748B] dark:text-[#A1A1AA]'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">
              API Oficial (Coexistente)
            </p>
            <p className="text-xs text-[#64748B] dark:text-[#71717A] mt-0.5">Login via Facebook / OAuth 2.0</p>
          </div>
          {selected === 'coexistent' && (
            <div className="w-2 h-2 rounded-full bg-[#2563EB] dark:bg-[#60A5FA] flex-shrink-0" />
          )}
        </button>
      </div>
    </div>
  );
};

const CoexistentApiSetup: React.FC = () => {
  const [step, setStep] = useState<'idle' | 'connecting' | 'connected'>('idle');

  const handleFacebookLogin = () => {
    setStep('connecting');
    setTimeout(() => {
      setStep('connected');
    }, 2000);
  };

  if (step === 'connecting') {
    return (
      <div className="flex min-h-[420px] flex-col items-center justify-center space-y-6 py-[15vh] text-center">
        <div className="w-12 h-12 border-4 border-[#F1F5F9] border-t-[#2563EB] rounded-full animate-spin dark:border-[#3F3F46] dark:border-t-[#60A5FA]" />
        <p className="text-[#0F172A] dark:text-[#FAFAFA]">Conectando com Facebook Business...</p>
        <p className="text-[#64748B] dark:text-[#71717A] text-sm">Aguarde enquanto autenticamos via OAuth 2.0</p>
      </div>
    );
  }

  if (step === 'connected') {
    return (
      <div className="flex min-h-[420px] flex-col items-center justify-center space-y-6 py-[15vh] text-center">
        <div className="w-16 h-16 bg-[#ECFDF5] rounded-full flex items-center justify-center dark:bg-green-500/15">
          <svg className="w-8 h-8 text-[#16A34A] dark:text-[#22C55E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="text-center">
          <h3 className="text-lg font-semibold text-[#0F172A] mb-2 dark:text-[#FAFAFA]">API Oficial Conectada</h3>
          <p className="text-[#475569] dark:text-[#A1A1AA] text-sm mb-1">WhatsApp Business API (Coexistente)</p>
          <p className="text-[#64748B] dark:text-[#71717A] text-xs mb-4">Conta: Meta Business Suite - Minha Empresa</p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => setStep('idle')}
              variant="destructive"
              className="min-w-[120px] rounded-xl bg-[#DC2626] hover:bg-[#B91C1C] text-white transition-colors duration-200 dark:bg-red-500/90 dark:hover:bg-red-500"
            >
              Desconectar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[420px] flex-col items-center justify-center space-y-6 py-[10vh] text-center">
      <div className="text-center mb-2">
        <h3 className="text-lg font-semibold text-[#0F172A] mb-2 dark:text-[#FAFAFA]">API Oficial WhatsApp</h3>
        <p className="text-[#475569] dark:text-[#A1A1AA] text-sm max-w-md">
          Conecte sua conta do WhatsApp Business usando a API Oficial via Facebook Business.
          A autenticacao e feita via OAuth 2.0.
        </p>
      </div>

      <SettingsCard className="p-5 max-w-md w-full space-y-4">
        <div className="flex items-start gap-3 text-left">
          <div className="w-6 h-6 rounded-full bg-[#DBEAFE] flex items-center justify-center flex-shrink-0 mt-0.5 dark:bg-blue-500/20">
            <span className="text-xs font-bold text-[#2563EB] dark:text-[#60A5FA]">1</span>
          </div>
          <div>
            <p className="text-sm text-[#0F172A] dark:text-[#FAFAFA]">Faca login na sua conta do Facebook Business</p>
            <p className="text-xs text-[#64748B] dark:text-[#71717A] mt-0.5">Voce sera redirecionado para o Facebook</p>
          </div>
        </div>
        <div className="flex items-start gap-3 text-left">
          <div className="w-6 h-6 rounded-full bg-[#DBEAFE] flex items-center justify-center flex-shrink-0 mt-0.5 dark:bg-blue-500/20">
            <span className="text-xs font-bold text-[#2563EB] dark:text-[#60A5FA]">2</span>
          </div>
          <div>
            <p className="text-sm text-[#0F172A] dark:text-[#FAFAFA]">Autorize o acesso ao WhatsApp Business</p>
            <p className="text-xs text-[#64748B] dark:text-[#71717A] mt-0.5">Selecione a conta e numero de telefone</p>
          </div>
        </div>
        <div className="flex items-start gap-3 text-left">
          <div className="w-6 h-6 rounded-full bg-[#DBEAFE] flex items-center justify-center flex-shrink-0 mt-0.5 dark:bg-blue-500/20">
            <span className="text-xs font-bold text-[#2563EB] dark:text-[#60A5FA]">3</span>
          </div>
          <div>
            <p className="text-sm text-[#0F172A] dark:text-[#FAFAFA]">Pronto! Sua API Oficial estara conectada</p>
            <p className="text-xs text-[#64748B] dark:text-[#71717A] mt-0.5">Templates e mensagens disponiveis</p>
          </div>
        </div>
      </SettingsCard>

      <Button
        onClick={handleFacebookLogin}
        className="min-w-[240px] rounded-xl bg-[#1877F2] hover:bg-[#166FE5] text-white font-medium py-3 flex items-center justify-center gap-2 transition-colors duration-200"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
        Continuar com Facebook
        <ChevronRight className="w-4 h-4" />
      </Button>

      <p className="text-[#64748B] dark:text-[#71717A] text-xs text-center max-w-sm">
        Ao conectar, voce autoriza o Rolt a enviar e receber mensagens pela API Oficial do WhatsApp Business.
      </p>
    </div>
  );
};

const ConnectionTab: React.FC = () => {
  const { user } = useAuth();
  const [apiType, setApiType] = useState<ConnectionApiType>('waha');
  // Versão da stack WAHA: 3 = GOWS (padrão para novas conexões), 2 = WAHA legado
  // (mantido apenas para contas que já foram criadas com connection_version = 2).
  const [wahaVersion, setWahaVersion] = useState<number>(3);

  // Resolve o nome da edge function conforme o tipo/versão da conexão:
  // WAHA GOWS (v3) para novas conexões, WAHA (v2) para contas legadas e
  // Evolution/coexistente (sem sufixo) para a API oficial.
  const resolveFunctionName = (base: string, forceVersion?: number): string => {
    if (apiType !== 'waha') return base;
    const v = forceVersion ?? wahaVersion;
    return `${base}-v${v === 2 ? 2 : 3}`;
  };
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
    hasInstance: false,
    isConnected: false,
    loading: true
  });
  const [disconnecting, setDisconnecting] = useState(false);
  const [creatingInstance, setCreatingInstance] = useState(false);

  const createInstance = async () => {
    if (!user) return;

    // Verificar variáveis de ambiente antes de fazer requisições
    const envCheck = checkEnvironmentVariables();
    if (!envCheck.isValid) {
      setConnectionStatus({
        hasInstance: false,
        isConnected: false,
        loading: false,
        error: `Configuração inválida: ${envCheck.error}. Verifique o arquivo .env`
      });
      return;
    }

    setCreatingInstance(true);
    setConnectionStatus(prev => ({ ...prev, loading: true, error: undefined }));

    try {
      console.log('🚀 Creating instance for user:', user.id);

      // Toda nova instância nasce na stack WAHA GOWS (v3), independentemente de
      // uma conexão v2 anterior ter deixado o estado com wahaVersion = 2.
      setApiType('waha');
      setWahaVersion(3);

      const functionName = resolveFunctionName('create-instance', 3);
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            account_id: user.account_id,
            name: user.name,
            email: user.email,
            phone: user.phone
          })
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Erro ao criar instância');
      }

      console.log('✅ Instance created successfully:', result.data);
      
      // Executar connect-instance imediatamente após sucesso (forçando v3, pois o
      // estado wahaVersion pode ainda não ter re-renderizado com o novo valor).
      console.log('🔄 Executing connect-instance after successful creation...');
      await executeConnectInstance(3);

    } catch (error) {
      console.error('❌ Erro ao criar instância:', error);
      setConnectionStatus({
        hasInstance: false,
        isConnected: false,
        loading: false,
        error: error instanceof Error ? error.message : 'Erro ao criar instância'
      });
    } finally {
      setCreatingInstance(false);
    }
  };

  const executeConnectInstance = async (forceVersion?: number) => {
    if (!user) return;

    // Retry mechanism for race condition handling
    const maxRetries = 3;
    const baseDelay = 1000; // 1 second
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await executeConnectInstanceAttempt(forceVersion);
        return; // Success, exit retry loop
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        
        // Only retry for "No instance found" errors
        if (errorMessage.includes('No instance found for this account') && attempt < maxRetries) {
          console.log(`🔄 Retry attempt ${attempt}/${maxRetries} for connect-instance in ${baseDelay * attempt}ms...`);
          await new Promise(resolve => setTimeout(resolve, baseDelay * attempt));
          continue;
        }
        
        // If it's not a retryable error or we've exhausted retries, throw the error
        throw error;
      }
    }
  };

  const executeConnectInstanceAttempt = async (forceVersion?: number) => {
    if (!user) return;

    // Verificar variáveis de ambiente antes de fazer requisições
    const envCheck = checkEnvironmentVariables();
    if (!envCheck.isValid) {
      setConnectionStatus({
        hasInstance: false,
        isConnected: false,
        loading: false,
        error: `Configuração inválida: ${envCheck.error}. Verifique o arquivo .env`
      });
      return;
    }

    try {
      console.log('🔌 Executing connect-instance for user:', user.id);
      
      const connectFn = resolveFunctionName('connect-instance', forceVersion);
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${connectFn}?account_id=${user.account_id}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          }
        }
      );

      const result = await response.json();

      if (!result.success) {
        console.error('❌ Connect-instance failed:', result.error);
        if (result.error === 'whatsapp_already_connected') {
          setConnectionStatus({
            hasInstance: true,
            isConnected: false,
            loading: false,
            conflict: {
              conflict_account_name: result.conflict_account_name,
              conflict_account_id: result.conflict_account_id,
              owner_jid: result.owner_jid
            }
          });
          return;
        }
        setConnectionStatus({
          hasInstance: true,
          isConnected: false,
          loading: false,
          error: result.error || 'Erro ao verificar status da conexão'
        });
        return;
      }

      const { data } = result;
      console.log('📱 Connect-instance response:', data);

      // Check if already connected
      if (data.instance && data.instance.state === 'open') {
        console.log('✅ Instance is connected, full data:', JSON.stringify(data));
        const ownerJid = data.instance?.ownerJid
          || data.instance?.owner
          || data.instance?.wuid
          || data.instance?.profilePictureUrl
          || data?.ownerJid
          || '';
        const ownerNumber = extractOwnerNumber(ownerJid);
        console.log('📞 Owner number extracted:', { ownerJid, ownerNumber });
        setConnectionStatus({
          hasInstance: true,
          isConnected: true,
          instanceName: data.instance.instanceName,
          ownerNumber: ownerNumber || undefined,
          loading: false
        });
      } else if (data.base64) {
        console.log('📱 Instance needs connection - showing QR code');
        setConnectionStatus({
          hasInstance: true,
          isConnected: false,
          qrCode: data.base64,
          loading: false
        });
      } else {
        console.log('❓ Unknown connection status');
        setConnectionStatus({
          hasInstance: true,
          isConnected: false,
          loading: false,
          error: 'Status de conexão desconhecido'
        });
      }

    } catch (error) {
      console.error('❌ Erro ao executar connect-instance:', error);
      setConnectionStatus({
        hasInstance: true,
        isConnected: false,
        loading: false,
        error: error instanceof Error ? error.message : 'Erro ao verificar conexão'
      });
    }
  };

  const checkConnectionStatus = async () => {
    if (!user) return;

    // Verificar variáveis de ambiente antes de fazer requisições
    const envCheck = checkEnvironmentVariables();
    if (!envCheck.isValid) {
      setConnectionStatus({
        hasInstance: false,
        isConnected: false,
        loading: false,
        error: `Configuração inválida: ${envCheck.error}. Verifique o arquivo .env`
      });
      return;
    }

    setConnectionStatus(prev => ({ ...prev, loading: true, error: undefined }));

    try {
      // Check if supabase is properly initialized
      if (!supabase) {
        throw new Error('Supabase não está configurado. Verifique as variáveis de ambiente VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY no arquivo .env');
      }

      console.log('🔍 Buscando instância para account_id:', user.account_id);
      console.log('📋 Dados do usuário completos:', {
        id: user.id,
        name: user.name,
        email: user.email,
        account_id: user.account_id
      });

      // Verificar auth do Supabase antes da query
      const { data: { session } } = await supabase.auth.getSession();
      console.log('🔐 Sessão do Supabase:', {
        has_session: !!session,
        user_id: session?.user?.id,
        expires_at: session?.expires_at
      });

      if (!session) {
        console.error('❌ Sessão do Supabase expirada ou inválida');
        throw new Error('Sessão expirada. Por favor, faça login novamente.');
      }

      // Debug: Testar get_auth_account_id()
      console.log('🔍 Testando get_auth_account_id()...');
      const { data: debugData, error: debugError } = await supabase
        .rpc('debug_user_access');

      if (!debugError && debugData) {
        console.log('📊 Debug Info:', debugData);
      }

      // Check if user has instance
      const { data: instanceData, error: instanceError } = await supabase
        .from('account_instances')
        .select('*')
        .eq('account_id', user.account_id)
        .maybeSingle();

      console.log('📥 Resultado da query account_instances:', {
        data: instanceData,
        error: instanceError,
        account_id_usado: user.account_id
      });

      if (instanceError) {
        console.error('❌ Erro do Supabase:', instanceError);

        // Se for erro de RLS, tentar diagnosticar
        if (instanceError.code === 'PGRST116' || instanceError.message.includes('permission')) {
          const diagMessage = `
🔒 ERRO DE PERMISSÃO (RLS)

O banco de dados bloqueou o acesso à tabela account_instances.

Possíveis causas:
1. A função get_auth_account_id() está retornando NULL
2. Sua sessão Supabase expirou
3. As políticas RLS estão muito restritivas

Informações de Debug:
- Account ID: ${user.account_id}
- Auth User ID: ${session?.user?.id || 'N/A'}
- Sessão expira em: ${session?.expires_at ? new Date(session.expires_at * 1000).toLocaleString('pt-BR') : 'N/A'}
${debugData ? `\nDebug Info:\n${JSON.stringify(debugData, null, 2)}` : ''}

Ação recomendada: Clique em "Renovar Sessão" abaixo.

Erro original: ${instanceError.message}
Código: ${instanceError.code || 'N/A'}
Detalhes: ${instanceError.details || 'N/A'}`;

          throw new Error(diagMessage);
        }

        throw new Error(`Erro ao buscar instância no banco de dados: ${instanceError.message}. Código: ${instanceError.code || 'N/A'}. Detalhes: ${instanceError.details || 'N/A'}`);
      }

      if (!instanceData) {
        console.log('📭 Nenhuma instância encontrada para account_id:', user.account_id);
        console.log('🔧 Criando instância automaticamente...');

        setConnectionStatus({
          hasInstance: false,
          isConnected: false,
          loading: true,
          error: undefined
        });

        await createInstance();
        return;
      }

      if (instanceData.connection_version === 2 || instanceData.connection_version === 3) {
        setApiType('waha');
        setWahaVersion(instanceData.connection_version);
      }

      console.log('✅ Instância encontrada:', {
        instance_name: instanceData.instance_name,
        instance_id: instanceData.instance_id,
        account_id: instanceData.account_id,
        connection_version: instanceData.connection_version
      });
      await executeConnectInstance();

    } catch (error) {
      console.error('Erro ao verificar status da conexão:', error);
      setConnectionStatus({
        hasInstance: false,
        isConnected: false,
        loading: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido'
      });
    }
  };

  const handleDisconnect = async () => {
    if (!user) return;

    // Verificar variáveis de ambiente antes de fazer requisições
    const envCheck = checkEnvironmentVariables();
    if (!envCheck.isValid) {
      setConnectionStatus(prev => ({
        ...prev,
        error: `Configuração inválida: ${envCheck.error}. Verifique o arquivo .env`
      }));
      return;
    }

    setDisconnecting(true);

    try {
      const disconnectFn = resolveFunctionName('disconnect-instance');
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${disconnectFn}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            account_id: user.account_id
          })
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Erro ao desconectar instância');
      }

      // Refresh connection status
      await checkConnectionStatus();

    } catch (error) {
      console.error('Erro ao desconectar:', error);
      setConnectionStatus(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro ao desconectar'
      }));
    } finally {
      setDisconnecting(false);
    }
  };

  const handleRefresh = () => {
    checkConnectionStatus();
  };

  const handleRenewSession = async () => {
    if (!user || !supabase) return;

    setConnectionStatus(prev => ({ ...prev, loading: true, error: undefined }));

    try {
      console.log('🔄 Renovando sessão Supabase...');

      // Forçar refresh do token
      const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();

      if (refreshError) {
        throw new Error(`Erro ao renovar sessão: ${refreshError.message}`);
      }

      if (!session) {
        throw new Error('Não foi possível renovar a sessão. Por favor, faça login novamente.');
      }

      console.log('✅ Sessão renovada com sucesso');
      console.log('🔐 Nova sessão expira em:', new Date(session.expires_at! * 1000).toLocaleString('pt-BR'));

      // Se for agency_user, atualizar a sessão RLS também
      if (user.type === 'agency_user' && user.account_id && supabase) {
        console.log('🔄 Atualizando sessão RLS para agency_user...');
        const { error: rlsError } = await supabase.rpc('update_agency_user_session', {
          p_account_id: user.account_id
        });

        if (rlsError) {
          console.error('⚠️ Erro ao atualizar sessão RLS:', rlsError);
        } else {
          console.log('✅ Sessão RLS atualizada');
        }
      }

      // Tentar verificar conexão novamente
      await checkConnectionStatus();

    } catch (error) {
      console.error('❌ Erro ao renovar sessão:', error);
      setConnectionStatus({
        hasInstance: false,
        isConnected: false,
        loading: false,
        error: error instanceof Error ? error.message : 'Erro ao renovar sessão'
      });
    }
  };

  // Apenas lê o status do banco, sem criar/iniciar a sessão no WAHA.
  // Assim, ao entrar na aba a conexão começa "offline" e o usuário decide
  // quando criar/conectar (evitando consumo de RAM desnecessário no WAHA).
  const loadInitialStatus = async () => {
    if (!user) return;

    const envCheck = checkEnvironmentVariables();
    if (!envCheck.isValid) {
      setConnectionStatus({
        hasInstance: false,
        isConnected: false,
        loading: false,
        error: `Configuração inválida: ${envCheck.error}. Verifique o arquivo .env`
      });
      return;
    }

    setConnectionStatus(prev => ({ ...prev, loading: true, error: undefined }));

    try {
      if (!supabase) {
        throw new Error('Supabase não está configurado. Verifique as variáveis de ambiente VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY no arquivo .env');
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Sessão expirada. Por favor, faça login novamente.');
      }

      const { data: instanceData, error: instanceError } = await supabase
        .from('account_instances')
        .select('*')
        .eq('account_id', user.account_id)
        .maybeSingle();

      if (instanceError) {
        throw new Error(`Erro ao buscar instância no banco de dados: ${instanceError.message}. Código: ${instanceError.code || 'N/A'}`);
      }

      if (instanceData?.connection_version === 2 || instanceData?.connection_version === 3) {
        setApiType('waha');
        setWahaVersion(instanceData.connection_version);
      }

      // Sem instância no banco → estado offline (com botão para criar/conectar)
      if (!instanceData) {
        setConnectionStatus({
          hasInstance: false,
          isConnected: false,
          loading: false
        });
        return;
      }

      // Instância já conectada → exibe nome e número, sem tocar no WAHA
      if (instanceData.is_connected) {
        const ownerNumber = extractOwnerNumber(instanceData.owner_jid);
        setConnectionStatus({
          hasInstance: true,
          isConnected: true,
          instanceName: instanceData.instance_name,
          ownerNumber: ownerNumber || undefined,
          loading: false
        });
        return;
      }

      // Instância existe mas está offline → não inicia sessão automaticamente
      setConnectionStatus({
        hasInstance: true,
        isConnected: false,
        loading: false
      });

    } catch (error) {
      console.error('Erro ao carregar status inicial da conexão:', error);
      setConnectionStatus({
        hasInstance: false,
        isConnected: false,
        loading: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido'
      });
    }
  };

  // Check status on component mount
  useEffect(() => {
    // Atualizar atividade do usuário
    updateLastActivity()
    loadInitialStatus();
  }, [user]);

  // Auto-refresh every 10 seconds when showing QR code
  useEffect(() => {
    if (connectionStatus.qrCode && !connectionStatus.isConnected) {
      const interval = setInterval(checkConnectionStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [connectionStatus.qrCode, connectionStatus.isConnected]);

  const renderEvolutionContent = () => {
    if (connectionStatus.loading || creatingInstance) {
      return (
        <div className="flex min-h-[420px] flex-col items-center justify-center space-y-6 py-[15vh] text-center">
          <div className="w-12 h-12 border-4 border-[#F1F5F9] border-t-[#2563EB] rounded-full animate-spin dark:border-[#3F3F46] dark:border-t-[#60A5FA]" />
          <p className="text-[#0F172A] dark:text-[#FAFAFA]">
            {creatingInstance ? 'Criando instância WhatsApp...' : 'Verificando status da conexão...'}
          </p>
        </div>
      );
    }

    if (connectionStatus.error) {
      const isRLSError = connectionStatus.error.includes('ERRO DE PERMISSÃO') ||
                         connectionStatus.error.includes('RLS');

      return (
        <div className="flex min-h-[420px] flex-col items-center justify-center space-y-6 py-[15vh] text-center">
          <div className="w-16 h-16 bg-[#FEF2F2] rounded-full flex items-center justify-center dark:bg-red-500/10">
            <svg className="w-8 h-8 text-[#DC2626] dark:text-[#F87171]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div className="text-center max-w-2xl">
            <h3 className="text-lg font-semibold text-[#0F172A] mb-2 dark:text-[#FAFAFA]">Erro na Conexão</h3>
            <pre className="text-[#475569] dark:text-[#A1A1AA] text-xs mb-4 text-left bg-[#F8FAFC] border border-[#E5E7EB] p-4 rounded-xl overflow-auto max-h-[400px] font-mono whitespace-pre-wrap dark:bg-[#18181B] dark:border-[#3F3F46]">
              {connectionStatus.error}
            </pre>
            <div className="flex gap-3 justify-center flex-wrap">
              {isRLSError && (
                <Button
                  onClick={handleRenewSession}
                  className="rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white min-w-[140px] transition-colors duration-200"
                >
                  Renovar Sessão
                </Button>
              )}
              <Button
                onClick={handleRefresh}
                className="rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white min-w-[120px] transition-colors duration-200"
              >
                Tentar Novamente
              </Button>
              <Button
                onClick={createInstance}
                variant="outline"
                className="min-w-[120px] rounded-xl border border-[#E5E7EB] bg-white text-[#334155] hover:bg-[#F1F5F9] hover:text-[#2563EB] transition-colors duration-200 dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46] dark:hover:text-[#60A5FA]"
                disabled={creatingInstance}
              >
                {creatingInstance ? 'Criando...' : 'Criar Instância'}
              </Button>
            </div>
          </div>
        </div>
      );
    }

  if (connectionStatus.isConnected) {
    return (
      <div className="flex min-h-[420px] flex-col items-center justify-center space-y-6 py-[15vh] text-center">
        <div className="w-16 h-16 bg-[#ECFDF5] rounded-full flex items-center justify-center dark:bg-green-500/15">
          <svg className="w-8 h-8 text-[#16A34A] dark:text-[#22C55E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="text-center">
          <h3 className="text-lg font-semibold text-[#0F172A] mb-2 dark:text-[#FAFAFA]">WhatsApp Conectado</h3>
          {connectionStatus.ownerNumber && (
            <p className="text-[#0F172A] dark:text-[#FAFAFA] text-base font-semibold mb-1">
              {formatPhoneNumber(connectionStatus.ownerNumber)}
            </p>
          )}
          <p className="text-[#64748B] dark:text-[#71717A] text-sm mb-4">
            Instância: {connectionStatus.instanceName}
          </p>
          <div className="flex gap-3 justify-center">
            <Button 
              onClick={handleDisconnect} 
              variant="destructive"
              disabled={disconnecting}
              className="min-w-[120px] rounded-xl bg-[#DC2626] hover:bg-[#B91C1C] text-white transition-colors duration-200 dark:bg-red-500/90 dark:hover:bg-red-500"
            >
              {disconnecting ? 'Desconectando...' : 'Desconectar'}
            </Button>
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="min-w-[120px] rounded-xl border border-[#E5E7EB] bg-white text-[#334155] hover:bg-[#F1F5F9] hover:text-[#2563EB] transition-colors duration-200 dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46] dark:hover:text-[#60A5FA]"
            >
              Atualizar
            </Button>
          </div>
        </div>
      </div>
    );
  }

    if (connectionStatus.qrCode) {
      return (
        <div className="flex min-h-[420px] flex-col items-center justify-center space-y-6 py-[10vh] text-center">
          <div className="text-center mb-4">
            <h3 className="text-lg font-semibold text-[#0F172A] mb-2 dark:text-[#FAFAFA]">Conectar WhatsApp</h3>
            <p className="text-[#475569] dark:text-[#A1A1AA] text-sm">
              Escaneie o QR Code com seu WhatsApp para conectar
            </p>
          </div>

          <div className="bg-white p-4 rounded-xl border border-[#E5E7EB] shadow-sm dark:bg-white dark:border-[#3F3F46] dark:shadow-none">
            <img
              src={connectionStatus.qrCode}
              alt="QR Code para conectar WhatsApp"
              className="w-[250px] h-[250px] object-contain"
            />
          </div>

          <div className="text-center">
            <p className="text-[#64748B] dark:text-[#71717A] text-xs mb-4">
              O QR Code será atualizado automaticamente a cada 30 segundos ou{' '}
              <button
                onClick={handleRefresh}
                className="text-[#2563EB] hover:text-[#1D4ED8] underline cursor-pointer transition-colors duration-200 dark:text-[#60A5FA] dark:hover:text-[#93C5FD]"
              >
                Atualizar
              </button>
              {' '}agora
            </p>
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="min-w-[140px] rounded-xl border border-[#E5E7EB] bg-white text-[#334155] hover:bg-[#F1F5F9] hover:text-[#2563EB] transition-colors duration-200 dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46] dark:hover:text-[#60A5FA]"
            >
              Atualizar Agora
            </Button>
          </div>
        </div>
      );
    }

    // Estado offline: instância não criada ou não conectada.
    // A sessão no WAHA só é iniciada quando o usuário clica em "Conectar".
    return (
      <div className="flex min-h-[420px] flex-col items-center justify-center space-y-6 py-[15vh] text-center">
        <div className="w-16 h-16 bg-[#F1F5F9] rounded-full flex items-center justify-center dark:bg-[#27272A]">
          <Power className="w-8 h-8 text-[#64748B] dark:text-[#A1A1AA]" />
        </div>
        <div className="text-center max-w-md">
          <h3 className="text-lg font-semibold text-[#0F172A] mb-2 dark:text-[#FAFAFA]">WhatsApp Offline</h3>
          <p className="text-[#475569] dark:text-[#A1A1AA] text-sm mb-1">
            Nenhuma conexão ativa no momento.
          </p>
          <p className="text-[#64748B] dark:text-[#71717A] text-xs mb-5">
            Clique no botão abaixo para iniciar a conexão e gerar o QR Code.
            A instância só é criada/iniciada após esta ação.
          </p>
          <Button
            onClick={checkConnectionStatus}
            disabled={creatingInstance}
            className="min-w-[200px] rounded-xl bg-[#16A34A] hover:bg-[#15803D] text-white shadow-sm transition-colors duration-200 dark:bg-[#22C55E] dark:hover:bg-[#16A34A]"
          >
            {creatingInstance ? 'Conectando...' : 'Conectar WhatsApp'}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <ApiTypeSelector selected={apiType} onSelect={setApiType} />
      {apiType === 'coexistent' ? <CoexistentApiSetup /> : renderEvolutionContent()}
    </div>
  );
};

export default ConnectionTab;